
import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Form, FormControl, FormField, FormItem, FormLabel } from '../components/ui/form';
import { useForm } from 'react-hook-form';
import { useToast } from '../hooks/use-toast';
import { 
  PlayCircle, RefreshCw, Check, AlertCircle, Code, BookOpen, FileText, 
  Settings, Download, Copy, RotateCcw, PanelLeft, Bug, Braces, Terminal,
  BookMarked, Search, GitBranch, Zap, Loader2, Info
} from 'lucide-react';
import { Badge } from "../components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import { ScrollArea } from "../components/ui/scroll-area";

// Mock user data - same as in other components
const CURRENT_USER = {
  name: 'John Doe',
  email: 'john.doe@example.com',
  role: 'student',
  avatar: null
};

// Sample starter code templates
const starterCode = {
  javascript: `// JavaScript practice code
function reverseString(str) {
  // Write your code here
  return '';
}

// Example usage
console.log(reverseString("hello"));`,

  typescript: `// TypeScript practice code
function addNumbers(a: number, b: number): number {
  // Write your code here
  return 0;
}

// Example usage
console.log(addNumbers(5, 10));`,

  python: `# Python practice code
def find_max(numbers):
    # Write your code here
    pass

# Example usage
print(find_max([1, 5, 8, 3, 9, 2]))`,

  csharp: `// C# practice code
using System;

public class Program
{
    public static int Factorial(int n)
    {
        // Write your code here
        return 0;
    }

    public static void Main()
    {
        Console.WriteLine(Factorial(5));
    }
}`,

  java: `// Java practice code
public class Main {
    public static int calculateSum(int[] numbers) {
        // Write your code here
        return 0;
    }

    public static void main(String[] args) {
        int[] nums = {1, 2, 3, 4, 5};
        System.out.println(calculateSum(nums));
    }
}`
};

// Sample test cases
const testCases = {
  javascript: [
    { input: 'hello', expected: 'olleh', description: 'Basic string reversal' },
    { input: 'JavaScript', expected: 'tpircSavaJ', description: 'Mixed case string' },
    { input: '12345', expected: '54321', description: 'Numeric string' }
  ],
  typescript: [
    { input: [5, 10], expected: 15, description: 'Positive numbers' },
    { input: [-3, 3], expected: 0, description: 'Sum to zero' },
    { input: [0, 0], expected: 0, description: 'Zero values' }
  ],
  python: [
    { input: [1, 5, 8, 3, 9, 2], expected: 9, description: 'Find maximum in list' },
    { input: [-5, -10, -3], expected: -3, description: 'Negative numbers' },
    { input: [7], expected: 7, description: 'Single element list' }
  ],
  csharp: [
    { input: 5, expected: 120, description: 'Factorial of 5' },
    { input: 0, expected: 1, description: 'Factorial of 0' },
    { input: 1, expected: 1, description: 'Factorial of 1' }
  ],
  java: [
    { input: [1, 2, 3, 4, 5], expected: 15, description: 'Sum of positive integers' },
    { input: [-1, -2, 3, 4], expected: 4, description: 'Mix of positive and negative' },
    { input: [], expected: 0, description: 'Empty array' }
  ]
};

// Mock syntax errors for code editor
const mockSyntaxCheck = (code: string, language: string): Array<{line: number, message: string, severity: 'error' | 'warning' | 'info'}> => {
  const errors:any[] = [];
  const lines = code.split('\n');
  
  // Simple syntax checks based on language
  switch(language) {
    case 'javascript':
    case 'typescript':
      // Check for missing semicolons
      lines.forEach((line, index) => {
        if (line.trim().length > 0 && 
            !line.trim().endsWith(';') && 
            !line.trim().endsWith('{') && 
            !line.trim().endsWith('}') && 
            !line.trim().startsWith('//') &&
            !line.trim().endsWith(')') &&
            !line.includes('function') &&
            !line.includes('if') &&
            !line.includes('for') &&
            !line.includes('while')) {
          errors.push({
            line: index + 1,
            message: 'Missing semicolon',
            severity: 'warning'
          });
        }
      });
      
      // Check for unbalanced brackets
      const stack :any[]= [];
      let lineNumber = 1;
      for (const char of code) {
        if (char === '{' || char === '(' || char === '[') {
          stack.push({ char, line: lineNumber });
        } else if (char === '}') {
          if (stack.length === 0 || stack.pop().char !== '{') {
            errors.push({
              line: lineNumber,
              message: 'Unbalanced curly bracket',
              severity: 'error'
            });
          }
        } else if (char === ')') {
          if (stack.length === 0 || stack.pop().char !== '(') {
            errors.push({
              line: lineNumber,
              message: 'Unbalanced parenthesis',
              severity: 'error'
            });
          }
        } else if (char === ']') {
          if (stack.length === 0 || stack.pop().char !== '[') {
            errors.push({
              line: lineNumber,
              message: 'Unbalanced square bracket',
              severity: 'error'
            });
          }
        } else if (char === '\n') {
          lineNumber++;
        }
      }
      
      // Check for missing variable declarations
      lines.forEach((line, index) => {
        if (line.includes('=') && 
            !line.includes('var ') && 
            !line.includes('let ') && 
            !line.includes('const ') && 
            !line.trim().startsWith('//')) {
          // Simple heuristic to detect possible variable assignments without declarations
          const parts = line.split('=');
          if (parts.length > 1 && !parts[0].includes('.') && !parts[0].includes('[')) {
            errors.push({
              line: index + 1,
              message: 'Consider adding a variable declaration (let, const, or var)',
              severity: 'info'
            });
          }
        }
      });
      break;
      
    case 'python':
      // Check for indentation issues
      let expectedIndent = 0;
      lines.forEach((line, index) => {
        if (line.trim().endsWith(':')) {
          expectedIndent += 4;
        } else if (line.trim() === '' && expectedIndent > 0) {
          expectedIndent -= 4;
        } else {
          const currentIndent = line.length - line.trimStart().length;
          if (line.trim().length > 0 && currentIndent !== expectedIndent) {
            errors.push({
              line: index + 1,
              message: `Indentation error. Expected ${expectedIndent} spaces.`,
              severity: 'error'
            });
          }
        }
      });
      break;
      
    case 'csharp':
    case 'java':
      // Check for missing semicolons
      lines.forEach((line, index) => {
        if (line.trim().length > 0 && 
            !line.trim().endsWith(';') && 
            !line.trim().endsWith('{') && 
            !line.trim().endsWith('}') && 
            !line.trim().startsWith('//') &&
            !line.includes('class') &&
            !line.includes('public') &&
            !line.includes('private')) {
          errors.push({
            line: index + 1,
            message: 'Missing semicolon',
            severity: 'warning'
          });
        }
      });
      break;
  }
  
  return errors;
};

// Mock function to simulate code execution
const executeCode = (code: string, language: string): Promise<{ output: string, success: boolean }> => {
  return new Promise((resolve) => {
    // In a real application, this would send the code to a backend service
    // For demo purposes, we'll simulate execution with setTimeout
    setTimeout(() => {
      const syntaxErrors = mockSyntaxCheck(code, language);
      const criticalErrors = syntaxErrors.filter(error => error.severity === 'error');
      
      if (criticalErrors.length > 0) {
        const errorOutput = criticalErrors.map(err => 
          `Line ${err.line}: ${err.message}`
        ).join('\n');
        
        resolve({ 
          output: `Execution failed:\n${errorOutput}`, 
          success: false 
        });
        return;
      }
      
      const output = `Executing ${language} code...\n\n`;
      
      // Sample outputs for different languages
      let sampleOutput = '';
      switch(language) {
        case 'javascript':
          sampleOutput = 'olleh\nExecution completed in 0.05s';
          break;
        case 'typescript':
          sampleOutput = '15\nExecution completed in 0.07s';
          break;
        case 'python':
          sampleOutput = '9\nExecution completed in 0.03s';
          break;
        case 'csharp':
          sampleOutput = '120\nExecution completed in 0.10s';
          break;
        case 'java':
          sampleOutput = '15\nExecution completed in 0.12s';
          break;
      }
      
      // Simulating success or errors randomly but more often success
      const success = Math.random() > 0.2;
      
      if (success) {
        resolve({ 
          output: output + `Output:\n> ${sampleOutput}`, 
          success: true 
        });
      } else {
        resolve({ 
          output: output + `Error:\n> Runtime error occurred.\n> Execution failed.`, 
          success: false 
        });
      }
    }, 1500);
  });
};

// Mock function to run test cases
const runTestCases = (code: string, language: string): Promise<{ results: Array<{ passed: boolean, message: string }> }> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      const cases = testCases[language as keyof typeof testCases] || [];
      
      // Simulate test results
      const results = cases.map(test => {
        const passed = Math.random() > 0.3;
        return {
          passed,
          message: passed 
            ? `✓ Test passed: ${test.description}`
            : `✗ Test failed: ${test.description}. Expected ${test.expected}, but got different result.`
        };
      });
      
      resolve({ results });
    }, 1000);
  });
};

// List of practice problems
const practiceProblems = {
  javascript: [
    { id: 'js1', title: 'String Reversal', difficulty: 'Easy' },
    { id: 'js2', title: 'Array Filtering', difficulty: 'Medium' },
    { id: 'js3', title: 'Object Manipulation', difficulty: 'Hard' }
  ],
  typescript: [
    { id: 'ts1', title: 'Type Inference', difficulty: 'Easy' },
    { id: 'ts2', title: 'Generic Functions', difficulty: 'Medium' },
    { id: 'ts3', title: 'Advanced Interfaces', difficulty: 'Hard' }
  ],
  python: [
    { id: 'py1', title: 'List Comprehension', difficulty: 'Easy' },
    { id: 'py2', title: 'Dictionary Operations', difficulty: 'Medium' },
    { id: 'py3', title: 'File Handling', difficulty: 'Hard' }
  ],
  csharp: [
    { id: 'cs1', title: 'LINQ Basics', difficulty: 'Easy' },
    { id: 'cs2', title: 'Async/Await', difficulty: 'Medium' },
    { id: 'cs3', title: 'Reflection', difficulty: 'Hard' }
  ],
  java: [
    { id: 'java1', title: 'Collections Framework', difficulty: 'Easy' },
    { id: 'java2', title: 'Multithreading', difficulty: 'Medium' },
    { id: 'java3', title: 'Stream API', difficulty: 'Hard' }
  ]
};

// Extension plugins for the code editor
const editorPlugins = [
  { id: 'syntax-highlight', name: 'Syntax Highlighting', active: true, icon: <Braces size={14} /> },
  { id: 'intellisense', name: 'IntelliSense', active: true, icon: <Zap size={14} /> },
  { id: 'prettier', name: 'Prettier', active: true, icon: <Code size={14} /> },
  { id: 'eslint', name: 'ESLint', active: true, icon: <AlertCircle size={14} /> },
  { id: 'git', name: 'Git Integration', active: false, icon: <GitBranch size={14} /> },
  { id: 'debugger', name: 'Debugger', active: false, icon: <Bug size={14} /> },
];

// Available editor themes
const editorThemes = [
  { id: 'vs-dark', name: 'Visual Studio Dark' },
  { id: 'monokai', name: 'Monokai' },
  { id: 'github-dark', name: 'GitHub Dark' },
  { id: 'night-owl', name: 'Night Owl' },
  { id: 'dracula', name: 'Dracula' },
];

// Function to add line numbers to code
const addLineNumbers = (code: string): string => {
  const lines = code.split('\n');
  return lines.map((line, index) => `${index + 1}. ${line}`).join('\n');
};

// Function to format code based on language
const formatCode = (code: string, language: string): string => {
  // In a real implementation, this would use a proper formatter like Prettier
  // This is just a simplified mock implementation
  return code;
};

// Function to highlight specific parts of code with syntax highlighting
const highlightSyntax = (code: string, language: string): JSX.Element => {
  // This is a simplified mock of syntax highlighting
  // In reality, you would use a library like Prism.js or highlight.js
  const lines = code.split('\n');
  
  return (
    <div className="font-mono text-sm whitespace-pre">
      {lines.map((line, lineIndex) => {
        // Simple regex-based highlighting
        let highlightedLine = line;
        
        // Comments
        if (language === 'javascript' || language === 'typescript' || language === 'csharp' || language === 'java') {
          highlightedLine = highlightedLine.replace(
            /(\/\/.*$)/g, 
            '<span style="color: #6A9955;">$1</span>'
          );
        } else if (language === 'python') {
          highlightedLine = highlightedLine.replace(
            /(#.*$)/g, 
            '<span style="color: #6A9955;">$1</span>'
          );
        }
        
        // Keywords
        const keywords = {
          javascript: ['function', 'return', 'const', 'let', 'var', 'if', 'else', 'for', 'while'],
          typescript: ['function', 'return', 'const', 'let', 'var', 'if', 'else', 'for', 'while', 'interface', 'type', 'extends'],
          python: ['def', 'return', 'if', 'else', 'for', 'while', 'class', 'import', 'from', 'as'],
          csharp: ['public', 'private', 'class', 'static', 'void', 'int', 'string', 'bool', 'return', 'using'],
          java: ['public', 'private', 'class', 'static', 'void', 'int', 'String', 'boolean', 'return', 'import'],
        };
        
        (keywords[language as keyof typeof keywords] || []).forEach(keyword => {
          const regex = new RegExp(`\\b${keyword}\\b`, 'g');
          highlightedLine = highlightedLine.replace(
            regex, 
            `<span style="color: #569CD6;">$&</span>`
          );
        });
        
        // Strings
        if (language === 'javascript' || language === 'typescript' || language === 'csharp' || language === 'java') {
          highlightedLine = highlightedLine.replace(
            /(".*?"|'.*?')/g, 
            '<span style="color: #CE9178;">$1</span>'
          );
        } else if (language === 'python') {
          highlightedLine = highlightedLine.replace(
            /(".*?"|'.*?')/g, 
            '<span style="color: #CE9178;">$1</span>'
          );
        }
        
        // Numbers
        highlightedLine = highlightedLine.replace(
          /\b(\d+)\b/g, 
          '<span style="color: #B5CEA8;">$1</span>'
        );
        
        // Types (for statically typed languages)
        if (language === 'typescript') {
          highlightedLine = highlightedLine.replace(
            /:\s*([A-Za-z]+)(\[\])?/g, 
            ': <span style="color: #4EC9B0;">$1$2</span>'
          );
        }
        
        return (
          <div key={lineIndex} className="flex">
            <span className="w-8 inline-block text-right mr-3 text-gray-500 select-none">
              {lineIndex + 1}
            </span>
            <span dangerouslySetInnerHTML={{ __html: highlightedLine }} />
          </div>
        );
      })}
    </div>
  );
};

export const CodePractice = () => {
  const [language, setLanguage] = useState<string>('javascript');
  const [code, setCode] = useState<string>(starterCode.javascript);
  const [output, setOutput] = useState<string>('');
  const [isRunning, setIsRunning] = useState<boolean>(false);
  const [isRunningTests, setIsRunningTests] = useState<boolean>(false);
  const [testResults, setTestResults] = useState<Array<{ passed: boolean, message: string }>>([]);
  const [selectedProblem, setSelectedProblem] = useState<string>('');
  const [activeTheme, setActiveTheme] = useState<string>('vs-dark');
  const [errors, setErrors] = useState<Array<{line: number, message: string, severity: 'error' | 'warning' | 'info'}>>([]);
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [activePlugins, setActivePlugins] = useState<string[]>(editorPlugins.filter(p => p.active).map(p => p.id));
  const [isFormatting, setIsFormatting] = useState<boolean>(false);
  const [showLineNumbers, setShowLineNumbers] = useState<boolean>(true);
  const [fontSize, setFontSize] = useState<number>(14);
  const { toast } = useToast();
  const editorRef = useRef<HTMLTextAreaElement>(null);
  const [user,setCurrentUser] = useState(null);
  
  const form = useForm({
    defaultValues: {
      language: 'javascript',
      problem: '',
      theme: 'vs-dark'
    }
  });

  useEffect(() => {
    // Update code when language changes
    setCode(starterCode[language as keyof typeof starterCode] || '');
    const userData = JSON.parse(localStorage?.getItem('user')!) ?? null;
    setOutput('');
    setTestResults([]);
    setCurrentUser(userData);
    CURRENT_USER.email=userData?.emailId;
    CURRENT_USER.name=userData?.studentsName;
    setErrors([]);
  }, [language]);

  useEffect(() => {
    // Check for syntax errors when code changes
    if (activePlugins.includes('eslint') || activePlugins.includes('intellisense')) {
      const timer = setTimeout(() => {
        setErrors(mockSyntaxCheck(code, language));
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [code, language, activePlugins]);

  const handleLanguageChange = (value: string) => {
    setLanguage(value);
  };

  const handleRunCode = async () => {
    setIsRunning(true);
    setOutput('Executing code...');
    
    try {
      const result = await executeCode(code, language);
      setOutput(result.output);
      
      if (result.success) {
        toast({
          title: "Code Execution Successful",
          description: "Your code ran without errors.",
        });
      } else {
        toast({
          title: "Code Execution Failed",
          description: "There were errors in your code.",
          variant: "destructive",
        });
      }
    } catch (error) {
      setOutput(`Error executing code: ${error}`);
      toast({
        title: "Execution Error",
        description: "An unexpected error occurred.",
        variant: "destructive",
      });
    } finally {
      setIsRunning(false);
    }
  };

  const handleRunTests = async () => {
    setIsRunningTests(true);
    
    try {
      const { results } = await runTestCases(code, language);
      setTestResults(results);
      
      const passedCount = results.filter(r => r.passed).length;
      toast({
        title: `${passedCount}/${results.length} Tests Passed`,
        description: passedCount === results.length 
          ? "All tests passed successfully!" 
          : "Some tests failed. Review the results.",
        variant: passedCount === results.length ? "default" : "destructive",
      });
    } catch (error) {
      toast({
        title: "Test Error",
        description: "An error occurred while running tests.",
        variant: "destructive",
      });
    } finally {
      setIsRunningTests(false);
    }
  };

  const handleResetCode = () => {
    setCode(starterCode[language as keyof typeof starterCode] || '');
    setOutput('');
    setTestResults([]);
    setErrors([]);
    toast({
      title: "Code Reset",
      description: "Your code has been reset to the starter template.",
    });
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(code);
    toast({
      title: "Code Copied",
      description: "Code has been copied to clipboard.",
    });
  };

  const handleFormatCode = () => {
    setIsFormatting(true);
    
    setTimeout(() => {
      setCode(formatCode(code, language));
      setIsFormatting(false);
      toast({
        title: "Code Formatted",
        description: "Your code has been formatted.",
      });
    }, 800);
  };

  const togglePlugin = (pluginId: string) => {
    if (activePlugins.includes(pluginId)) {
      setActivePlugins(activePlugins.filter(id => id !== pluginId));
    } else {
      setActivePlugins([...activePlugins, pluginId]);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        type: 'spring',
        damping: 12,
        stiffness: 100
      }
    }
  };

  // Theme colors based on selected theme
  const getThemeColors = () => {
    switch(activeTheme) {
      case 'monokai':
        return {
          background: '#272822',
          foreground: '#F8F8F2',
          gutter: '#3E3D32'
        };
      case 'github-dark':
        return {
          background: '#0D1117',
          foreground: '#C9D1D9',
          gutter: '#161B22'
        };
      case 'night-owl':
        return {
          background: '#011627',
          foreground: '#D6DEEB',
          gutter: '#01111D'
        };
      case 'dracula':
        return {
          background: '#282A36',
          foreground: '#F8F8F2',
          gutter: '#343746'
        };
      case 'vs-dark':
      default:
        return {
          background: '#1E1E1E',
          foreground: '#D4D4D4',
          gutter: '#252526'
        };
    }
  };

  const themeColors = getThemeColors();

  return (
    <motion.div 
      className="space-y-8 animate-fade-in"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Header with user info and quote */}
      <motion.div 
        className="bg-gradient-to-r from-bytesplash-primary to-gray-600 text-white p-6 rounded-xl shadow-md mb-8"
        variants={itemVariants}
      >
        <div className="flex flex-col md:flex-row justify-between items-center">
          <div>
            {/* <h1 className="text-2xl font-bold mb-2">Hello, {CURRENT_USER.name}!</h1> */}
            <p className="text-2xl font-bold mb-2"><b>{CURRENT_USER.email}</b></p>
          </div>
          <div className="mt-4 md:mt-0 text-center md:text-right">
            <p className="italic text-blue-100">"Good code is its own best documentation."</p>
            <p className="text-blue-200 mt-1">- Steve McConnell</p>
          </div>
        </div>
      </motion.div>

      <motion.div variants={itemVariants}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Code Practice</h1>
            <p className="text-bytesplash-gray-500">Write, run, and test your code with our VS Code-inspired editor</p>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setShowSettings(!showSettings)}
            className="flex items-center gap-2"
          >
            <Settings size={16} />
            <span>Settings</span>
          </Button>
        </div>
      </motion.div>

      <motion.div variants={itemVariants}>
        <Form {...form}>
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <FormField
              control={form.control}
              name="language"
              render={({ field }) => (
                <FormItem className="w-full sm:w-1/3">
                  <FormLabel>Programming Language</FormLabel>
                  <Select
                    value={language}
                    onValueChange={(value) => {
                      handleLanguageChange(value);
                      field.onChange(value);
                    }}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a language" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="javascript">JavaScript</SelectItem>
                      <SelectItem value="typescript">TypeScript</SelectItem>
                      <SelectItem value="python">Python</SelectItem>
                      <SelectItem value="csharp">C#</SelectItem>
                      <SelectItem value="java">Java</SelectItem>
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="problem"
              render={({ field }) => (
                <FormItem className="w-full sm:w-2/3">
                  <FormLabel>Practice Problem</FormLabel>
                  <Select
                    value={selectedProblem}
                    onValueChange={(value) => {
                      setSelectedProblem(value);
                      field.onChange(value);
                    }}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a problem" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {practiceProblems[language as keyof typeof practiceProblems]?.map((problem) => (
                        <SelectItem key={problem.id} value={problem.id}>
                          {problem.title} - {problem.difficulty}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
          </div>
        </Form>
      </motion.div>

      {/* Settings Panel */}
      {showSettings && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="mb-6"
        >
          <Card className="shadow-md border-bytesplash-primary/20">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Editor Settings</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <h3 className="font-medium mb-2 text-sm">Theme</h3>
                  <Select
                    value={activeTheme}
                    onValueChange={setActiveTheme}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select a theme" />
                    </SelectTrigger>
                    <SelectContent>
                      {editorThemes.map(theme => (
                        <SelectItem key={theme.id} value={theme.id}>
                          {theme.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <h3 className="font-medium mb-2 text-sm">Font Size</h3>
                  <Select
                    value={fontSize.toString()}
                    onValueChange={(value) => setFontSize(parseInt(value))}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select font size" />
                    </SelectTrigger>
                    <SelectContent>
                      {[12, 14, 16, 18, 20].map(size => (
                        <SelectItem key={size} value={size.toString()}>
                          {size}px
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <h3 className="font-medium mb-2 text-sm">Display Options</h3>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="line-numbers"
                      checked={showLineNumbers}
                      onChange={() => setShowLineNumbers(!showLineNumbers)}
                      className="rounded border-gray-300"
                    />
                    <label htmlFor="line-numbers" className="text-sm">Show line numbers</label>
                  </div>
                </div>
              </div>
              
              <div className="mt-6">
                <h3 className="font-medium mb-2 text-sm">Extensions</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2">
                  {editorPlugins.map(plugin => (
                    <div
                      key={plugin.id}
                      onClick={() => togglePlugin(plugin.id)}
                      className={`flex items-center gap-2 p-2 rounded-md border cursor-pointer transition-colors ${
                        activePlugins.includes(plugin.id)
                          ? 'border-bytesplash-primary bg-bytesplash-light text-bytesplash-primary'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      {plugin.icon}
                      <span className="text-xs">{plugin.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}
      
      <motion.div variants={itemVariants} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="shadow-md border-[#333333]">
            <CardHeader className={`pb-2 border-b border-[#333333] bg-[${themeColors.background}] text-[${themeColors.foreground}]`}>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-[#CCCCCC]">
                  <Code size={18} className="text-[#007ACC]" />
                  <span className="font-medium">Code Editor</span>
                  <Badge variant="outline" className="ml-2 text-xs font-normal">
                    {language}
                  </Badge>
                </CardTitle>
                <div className="flex gap-1">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-7 w-7 rounded-sm text-[#CCCCCC] hover:text-white hover:bg-[#1F1F1F]"
                          onClick={handleCopyCode}
                        >
                          <Copy size={14} />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-xs">Copy code</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          disabled={isFormatting}
                          className="h-7 w-7 rounded-sm text-[#CCCCCC] hover:text-white hover:bg-[#1F1F1F]"
                          onClick={handleFormatCode}
                        >
                          {isFormatting ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <Braces size={14} />
                          )}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-xs">Format code</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-7 w-7 rounded-sm text-[#CCCCCC] hover:text-white hover:bg-[#1F1F1F]"
                          onClick={handleResetCode}
                        >
                          <RotateCcw size={14} />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-xs">Reset code</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0 relative">
              <div 
                className={`relative font-mono h-[400px] overflow-auto`}
                style={{ 
                  backgroundColor: themeColors.background,
                  color: themeColors.foreground, 
                  fontSize: `${fontSize}px`,
                }}
              >
                {activePlugins.includes('syntax-highlight') ? (
                  <ScrollArea className="h-[400px] w-full">
                    <div className="p-4">
                      {highlightSyntax(code, language)}
                    </div>
                  </ScrollArea>
                ) : (
                  <Textarea
                    ref={editorRef}
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    enableCodeEditor
                    language={language as any}
                    className="h-[400px] resize-none p-4 rounded-none"
                    style={{ 
                      backgroundColor: themeColors.background,
                      color: themeColors.foreground,
                      fontSize: `${fontSize}px`, 
                      lineHeight: '1.5',
                      fontFamily: 'monospace'
                    }}
                  />
                )}
                
                {/* Error/warning indicators */}
                {activePlugins.includes('eslint') && errors.length > 0 && (
                  <div className="absolute right-2 top-2 flex flex-col gap-2">
                    {errors.filter(e => e.severity === 'error').length > 0 && (
                      <Badge variant="destructive" className="flex items-center gap-1">
                        <AlertCircle size={10} />
                        {errors.filter(e => e.severity === 'error').length} error(s)
                      </Badge>
                    )}
                    {errors.filter(e => e.severity === 'warning').length > 0 && (
                      <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-300 flex items-center gap-1">
                        <AlertCircle size={10} />
                        {errors.filter(e => e.severity === 'warning').length} warning(s)
                      </Badge>
                    )}
                  </div>
                )}
              </div>
              
              {/* Error details */}
              {activePlugins.includes('eslint') && errors.length > 0 && (
                <div className="bg-[#252526] border-t border-[#333333] text-white p-2 max-h-36 overflow-auto">
                  <div className="text-xs font-mono space-y-1">
                    {errors.map((error, index) => (
                      <div key={index} className="flex items-start gap-2">
                        <span className={`${
                          error.severity === 'error' ? 'text-red-500' : 
                          error.severity === 'warning' ? 'text-yellow-500' : 'text-blue-500'
                        } flex-shrink-0`}>
                          {error.severity === 'error' ? <AlertCircle size={12} /> : 
                           error.severity === 'warning' ? <AlertCircle size={12} /> : <Info size={12} />}
                        </span>
                        <span>Line {error.line}: {error.message}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <CardFooter className="bg-[#007ACC] text-white p-2 flex justify-between items-center">
                <div className="flex gap-4 text-xs">
                  <span>{language.charAt(0).toUpperCase() + language.slice(1)}</span>
                  <span>Line: {code.split('\n').length}</span>
                </div>
                <div className="flex gap-2">
                  <Button 
                    onClick={handleRunCode} 
                    disabled={isRunning}
                    size="sm"
                    className="bg-[#0E639C] hover:bg-[#1177bb] text-white border-none flex items-center gap-2 h-8"
                  >
                    {isRunning ? <RefreshCw className="animate-spin" size={14} /> : <PlayCircle size={14} />}
                    <span>{isRunning ? 'Running...' : 'Run Code'}</span>
                  </Button>
                  <Button 
                    onClick={handleRunTests} 
                    disabled={isRunningTests}
                    size="sm"
                    variant="outline"
                    className="bg-transparent text-white border-white/30 hover:bg-white/10 flex items-center gap-2 h-8"
                  >
                    {isRunningTests ? <RefreshCw className="animate-spin" size={14} /> : <Check size={14} />}
                    <span>{isRunningTests ? 'Running Tests...' : 'Run Tests'}</span>
                  </Button>
                </div>
              </CardFooter>
            </CardContent>
          </Card>
        </div>
        
        <div className="space-y-6">
          <Tabs defaultValue="output" className="h-full flex flex-col">
            <TabsList className="w-full grid grid-cols-2">
              <TabsTrigger value="output" className="flex items-center gap-2">
                <Terminal size={14} />
                Output
              </TabsTrigger>
              <TabsTrigger value="tests" className="flex items-center gap-2">
                <Check size={14} />
                Test Results
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="output" className="flex-grow">
              <Card className="shadow-md border-[#333333] h-full">
                <CardContent className="p-0 h-full">
                  <div 
                    className="font-mono text-sm bg-[#1E1E1E] text-[#CCCCCC] p-4 rounded-md h-[400px] overflow-auto whitespace-pre-wrap"
                    style={{ fontFamily: 'Consolas, monospace' }}
                  >
                    {output || (
                      <div className="text-gray-500 flex flex-col items-center justify-center h-full">
                        <Terminal size={24} className="mb-2" />
                        <p>No output yet. Run your code to see results.</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="tests" className="flex-grow">
              <Card className="shadow-md border-[#333333] h-full">
                <CardContent className="p-0 h-full">
                  <div className="bg-[#1E1E1E] text-white p-4 rounded-md h-[400px] overflow-auto">
                    {testResults.length > 0 ? (
                      <div className="space-y-2">
                        {testResults.map((result, index) => (
                          <div 
                            key={index} 
                            className={`p-3 rounded-md flex items-start gap-2 ${
                              result.passed ? 'bg-green-900/20 text-green-400 border border-green-900/30' : 'bg-red-900/20 text-red-400 border border-red-900/30'
                            }`}
                          >
                            {result.passed ? (
                              <Check className="shrink-0 mt-0.5" size={14} />
                            ) : (
                              <AlertCircle className="shrink-0 mt-0.5" size={14} />
                            )}
                            <div className="text-sm">{result.message}</div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center text-gray-400 h-full flex items-center justify-center">
                        <div>
                          <BookOpen className="mx-auto mb-2" size={24} />
                          <p>No test results yet. Run tests to see results.</p>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
          
          <Card className="shadow-md border-[#333333]">
            <CardHeader className="bg-[#1E1E1E] text-white pb-3 border-b border-[#333333]">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <BookMarked size={16} className="text-[#007ACC]" />
                Instructions
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 bg-[#252526] text-[#CCCCCC]">
              <div className="text-sm space-y-2">
                <p>1. Select a programming language and practice problem.</p>
                <p>2. Write your solution in the VS Code-like editor above.</p>
                <p>3. Use the "Run Code" button to execute your code and see output.</p>
                <p>4. Run tests to check if your solution passes all test cases.</p>
                <p>5. Use the editor settings to customize your experience.</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="shadow-md border-[#333333]">
            <CardHeader className="bg-[#1E1E1E] text-white pb-3 border-b border-[#333333]">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <PanelLeft size={16} className="text-[#007ACC]" />
                Active Extensions
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 bg-[#252526] text-[#CCCCCC]">
              <div className="grid grid-cols-2 gap-2">
                {activePlugins.map(pluginId => {
                  const plugin = editorPlugins.find(p => p.id === pluginId);
                  if (!plugin) return null;
                  
                  return (
                    <div key={pluginId} className="text-xs flex items-center gap-1.5 text-green-400">
                      <Check size={12} />
                      <span>{plugin.name}</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      </motion.div>
    </motion.div>
  );
};
